/**
 * WorkspaceTabs — right-rail tabbed surface.
 *   • Chat       — the existing ChatInterface
 *   • Dossier    — SCADA asset/region details (existing SCADAPanel inline)
 *   • Analytics  — existing AnalyticsPanel
 *
 * Auto-activates the most recently populated tab so a query lands the user
 * on the answer. Defaults to Chat when nothing else has been touched.
 */
import { useEffect, useState } from 'react';

export default function WorkspaceTabs({
  chat,
  dossier,
  analytics,
  databricks,           // <DatabricksPanel isOpen .../>
  audit,                // <AuditPanel />
  onboarding,           // <OnboardingPanel />
  hasDossier,
  hasAnalytics,
}) {
  const [active, setActive] = useState('chat');
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => { if (hasDossier)   { setLastUpdated('dossier');   setActive('dossier');   } }, [hasDossier]);
  useEffect(() => { if (hasAnalytics) { setLastUpdated('analytics'); setActive('analytics'); } }, [hasAnalytics]);

  return (
    <div className="ws-tabs">
      <div className="ws-tabbar">
        <TabButton id="chat" label="Chat" icon="💬" active={active === 'chat'} onClick={setActive} />
        <TabButton
          id="dossier"
          label="Dossier"
          icon="⚡"
          active={active === 'dossier'}
          fresh={hasDossier && lastUpdated === 'dossier'}
          onClick={setActive}
          tone="scada"
        />
        <TabButton
          id="analytics"
          label="Analytics"
          icon="📊"
          active={active === 'analytics'}
          fresh={hasAnalytics && lastUpdated === 'analytics'}
          onClick={setActive}
          tone="analytics"
        />
        {databricks && (
          <TabButton
            id="databricks"
            label="Databricks"
            icon="🧱"
            active={active === 'databricks'}
            onClick={setActive}
            tone="databricks"
          />
        )}
        {audit && (
          <TabButton
            id="audit"
            label="Audit"
            icon="📋"
            active={active === 'audit'}
            onClick={setActive}
            tone="default"
          />
        )}
        {onboarding && (
          <TabButton
            id="onboarding"
            label="Onboard"
            icon="➕"
            active={active === 'onboarding'}
            onClick={setActive}
            tone="default"
          />
        )}
      </div>

      <div className="ws-content">
        <div className="ws-pane" hidden={active !== 'chat'}>{chat}</div>
        <div className="ws-pane" hidden={active !== 'dossier'}>
          {hasDossier ? dossier : (
            <EmptyPane
              icon="⚡"
              title="No SCADA query active"
              hint='Run "show critical alerts in Reeves" or click an asset on the map / left rail.'
            />
          )}
        </div>
        <div className="ws-pane" hidden={active !== 'analytics'}>
          {hasAnalytics ? analytics : (
            <EmptyPane
              icon="📊"
              title="No analytics yet"
              hint='Run "summarize disposal wells by operator" or "do all analytics".'
            />
          )}
        </div>
        {databricks && (
          <div className="ws-pane ws-pane-databricks" hidden={active !== 'databricks'}>
            {databricks}
          </div>
        )}
        {audit && (
          <div className="ws-pane" hidden={active !== 'audit'} style={{ padding: 0 }}>
            {audit}
          </div>
        )}
        {onboarding && (
          <div className="ws-pane" hidden={active !== 'onboarding'} style={{ padding: 0 }}>
            {onboarding}
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({ id, label, icon, active, fresh, tone = 'default', onClick }) {
  return (
    <button
      className={`ws-tab ws-tab-${tone} ${active ? 'active' : ''}`}
      onClick={() => onClick(id)}
    >
      <span className="ws-tab-icon">{icon}</span>
      <span className="ws-tab-label">{label}</span>
      {fresh && <span className={`ws-tab-dot ws-tab-dot-${tone}`} />}
    </button>
  );
}

function EmptyPane({ icon, title, hint }) {
  return (
    <div className="ws-empty">
      <div className="ws-empty-icon">{icon}</div>
      <div className="ws-empty-title">{title}</div>
      <div className="ws-empty-hint">{hint}</div>
    </div>
  );
}
